import React, {useCallback, useState} from 'react';
import createAxiosInstance from '../../../support/AxiosSupport';
import {IMAGE_ACCEPT, splitAcceptedImages, uniqueFileId} from '../../../hooks/usePasteImages';
import usePhotoProfilingConsent from '../../../hooks/usePhotoProfilingConsent';
import Spinner from '../../Spinner';
import {CameraIcon, ShieldIcon, XIcon} from '../../icons';
import {FIRST_ITEM_MAX_PHOTOS} from '../constants';
import {StepProps, asRecord} from './types';

interface PhotoSlot {
    id: string;
    key?: string;
    previewUrl: string;
    uploading: boolean;
    error?: string;
}

const errorText = (err: unknown): string => {
    const e = err as Error & {response?: {data?: {description?: string; message?: string}}};
    return e?.response?.data?.description || e?.response?.data?.message || 'Не удалось загрузить';
};

/**
 * Шаг «Первые вещи» (слот 12): фото → AI-распознавание. Путь тот же, что в
 * `AddAiRecognitionModal`: `POST /api/clothing/images` на каждый файл, затем один
 * `POST /api/ai-recognition` с ключами фото. Отправку шаг делает сам (кнопкой в карточке),
 * а в ответ кладёт число отправленных фото — чтобы возобновление мастера не предлагало
 * загрузить их повторно.
 *
 * Анализ фото — opt-in (GDPR ст. 22): без согласия `photoProfilingConsent` загрузка
 * недоступна, шаг объясняет это и остаётся пропускаемым.
 */
const StepFirstItem: React.FC<StepProps> = ({value, onChange}) => {
    const {granted, loading: consentLoading} = usePhotoProfilingConsent();
    const [slots, setSlots] = useState<PhotoSlot[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const submitted = Number(asRecord(value).photoCount ?? 0);

    const uploadFiles = useCallback(async (files: File[]) => {
        const {accepted, rejected} = splitAcceptedImages(files);
        if (rejected.length > 0) {
            setError(`${rejected[0].file.name}: ${rejected[0].reason}`);
        }
        if (accepted.length === 0) {
            return;
        }
        const api = await createAxiosInstance();
        const newSlots: PhotoSlot[] = accepted.slice(0, FIRST_ITEM_MAX_PHOTOS).map(file => ({
            id: `photo-${uniqueFileId()}`,
            previewUrl: URL.createObjectURL(file),
            uploading: true,
        }));
        setSlots(prev => [...prev, ...newSlots].slice(0, FIRST_ITEM_MAX_PHOTOS));

        for (let i = 0; i < newSlots.length; i++) {
            const slot = newSlots[i];
            const formData = new FormData();
            formData.append('file', accepted[i]);
            try {
                const res = await api.post<{key: string}>('/clothing/images', formData,
                    {headers: {'Content-Type': 'multipart/form-data'}});
                setSlots(prev => prev.map(s => (s.id === slot.id
                    ? {...s, key: res.data.key, uploading: false}
                    : s)));
            } catch (err) {
                setSlots(prev => prev.map(s => (s.id === slot.id
                    ? {...s, uploading: false, error: errorText(err)}
                    : s)));
            }
        }
    }, []);

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        setError(null);
        if (files.length > 0) {
            uploadFiles(files);
        }
    };

    const removeSlot = (id: string) => {
        setSlots(prev => {
            const slot = prev.find(s => s.id === id);
            if (slot) {
                URL.revokeObjectURL(slot.previewUrl);
            }
            return prev.filter(s => s.id !== id);
        });
    };

    const readyKeys = slots.filter(s => s.key && !s.error).map(s => s.key as string);
    const uploading = slots.some(s => s.uploading);

    const submit = async () => {
        if (readyKeys.length === 0 || uploading || submitting) {
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            await api.post('/ai-recognition', {photoKeys: readyKeys, description: 'Онбординг'});
            onChange({photoCount: submitted + readyKeys.length});
            slots.forEach(s => URL.revokeObjectURL(s.previewUrl));
            setSlots([]);
        } catch (err) {
            setError(errorText(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (consentLoading) {
        return <Spinner/>;
    }

    if (!granted) {
        return (
            <div className="flex items-start gap-3" data-testid="onboarding-first-item-consent-required">
                <ShieldIcon className="w-5 h-5 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5"/>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    Распознавание вещей по фото требует согласия на анализ фотографий. Вернитесь на
                    шаг «Согласия», чтобы включить его, или пропустите шаг — вещи можно добавить позже.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="onboarding-step-first-item">
            {submitted > 0 && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400" data-testid="onboarding-first-item-sent">
                    Отправлено на распознавание фото: {submitted}. Результат появится в разделе
                    «AI распознавание».
                </p>
            )}

            <label className="flex items-center gap-3 min-h-[44px] w-full rounded-xl border border-dashed border-gray-300 dark:border-ink-700 px-4 py-3 cursor-pointer hover:border-brand-500 transition-colors">
                <CameraIcon className="w-5 h-5 text-brand-600 dark:text-brand-400"/>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                    Выбрать фото (до {FIRST_ITEM_MAX_PHOTOS} за раз)
                </span>
                <input
                    type="file"
                    accept={IMAGE_ACCEPT}
                    multiple
                    onChange={handleFiles}
                    className="sr-only"
                    data-testid="onboarding-first-item-input"
                />
            </label>

            {slots.length > 0 && (
                <ul className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="onboarding-first-item-slots">
                    {slots.map(slot => (
                        <li key={slot.id} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 dark:border-ink-700">
                            <img src={slot.previewUrl} alt="" className="w-full h-full object-cover"/>
                            {slot.uploading && (
                                <span className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-ink-900/70 text-xs text-gray-700 dark:text-gray-200">
                                    Загрузка…
                                </span>
                            )}
                            {slot.error && (
                                <span className="absolute inset-0 flex items-center justify-center bg-rose-50/90 dark:bg-rose-900/60 text-xs text-rose-700 dark:text-rose-200 p-1 text-center">
                                    {slot.error}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={() => removeSlot(slot.id)}
                                aria-label="Убрать фото"
                                className="absolute top-1 right-1 min-h-[28px] min-w-[28px] inline-flex items-center justify-center rounded-lg bg-white/90 dark:bg-ink-800/90 text-gray-600 dark:text-gray-300"
                                data-testid={`onboarding-first-item-remove-${slot.id}`}
                            >
                                <XIcon className="w-4 h-4"/>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <button
                type="button"
                onClick={submit}
                disabled={readyKeys.length === 0 || uploading || submitting}
                className="btn-secondary min-h-[44px]"
                data-testid="onboarding-first-item-submit"
            >
                {submitting ? 'Отправка…' : 'Отправить на распознавание'}
            </button>

            {error && (
                <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="onboarding-first-item-error">
                    {error}
                </p>
            )}
        </div>
    );
};

export default StepFirstItem;
